
import AppMobileFullScreenContainer from "@/components/common/MobileFullScreenContainer.vue";
import AppToolbar from "@/components/common/ToolbarBase.vue";
import { UserInfo } from "../../../models/dossier/user-info";
import { DossierService } from "../../../services/dossier-service";
import { IBreadCrumb } from "winfakt-vue-components";
import { AuthService } from "@/services/auth-service";
import { CompanyService } from "@/services/company-service";
import AppPermissions from "@/components/settings-default/UserPermissions.vue";
import { Permissions } from "@/models/user-configs/permissions";
import { UserConfigService } from "../../../services/user-config-service";
import { FormLike } from "winfakt-vue-components/src/mixins/form-like";
import AppErrorMessage from "@/components/common/ErrorMessageBox.vue";
import { isStringEmail, isPasswordStrongEnough, isIpAddressValid } from "../../../utils/strings";
import AppChangeProfilePicture from "@/components/settings-default/UserEditChangeProfilePicture.vue";
import AppUserEditIpRestriction from "./UserEditIpRestriction.vue";

export default FormLike.extend({
	data(){
		return {
			changeProfilePictureModal: false,
			inputs: {
				username: "",
				email: "",
				firstName: "",
				lastName: "",
				password: "",
				repeatPassword: ""
			},
			usedInputs: {
				email: false,
				password: false,
				repeatPassword: false
			},
			hasError: {
				email: false,
				password: false,
				repeatPassword: false
			},
			formErrors: [] as string[],
			user: new UserInfo(),
			loading: true,
			dossierAccess: true,
			hasDossierAccess: true,
			isSaving: false,
			permissions: null as null | Permissions,
			newProfilePicture: null as null | File,
			newProfilePictureUrl: ""
		};
	},
	watch: {
		hasDossierAccess: {
			immediate: true,
			async handler(){
				if (this.hasDossierAccess && this.user.ID != 0){
					this.permissions = await UserConfigService.getUsersPermissions(this.user.ID);
				}else{
					this.permissions = null;
				}
			}
		},
		inputs: {
			deep: true,
			handler(){
				this.formErrors = [];
			}
		},
		usedInputs: {
			deep: true,
			handler(){
				this.formErrors = [];
				this.validate();
			}
		},
		"user.RestrictedToIPAddresses": {
			deep: true,
			handler(){
				this.validate();
			}
		},
		isOwner():boolean{
			return this.user.ID == AuthService.wfCompany.OwnerID;
		},
	},
	computed: {
		path():IBreadCrumb[] {
			return [
				{
					to: "/settings/",
					value: this.$ct("general")
				},
				{
					to: "/settings/users",
					value: this.$ct("users")
				},
				{
					value: this.title
				}
			] as IBreadCrumb[];
		},
		title():string{
			return this.$ct(this.user.ID == 0 ? "create-user" : "edit-user").toString();
		},
		passwordsMatch():boolean{
			return this.inputs.password == this.inputs.repeatPassword;
		},
		emailRequired():boolean{
			return AuthService.wfCompany.WorkspaceURL == "" || AuthService.wfCompany.WorkspaceURL == null;
		},
		passwordRequired():boolean {
			return this.user.ID == 0;
		},
		userNameDissabled():boolean {
			return AuthService.wfCompany.WorkspaceURL == null;
		},
		saveDisabled():boolean {
			if (this.isSaving){
				return true;
			}
			if (!this.isValid){
				return true;
			}
			return false;
		},
		profileImageUrl():string {
			if (this.newProfilePictureUrl) {
				return this.newProfilePictureUrl;
			}
			return this.user.getProfileImgUrl();
		},
		isOwner():boolean {
			return this.user.ID == AuthService.wfCompany.OwnerID;
		},
		showPermissions():boolean {
			if (!this.permissions) {
				return false;
			}
			return true;
		}
	},
	methods: {
		cancel(){
			this.$router.push("/settings/users");
		},
		async submit(){
			if (!this.isValid) return;
			if (this.isSaving) return;
			this.isSaving = true;
			let user = this.setUserFromInputs();
			try{
				await this.saveUser(user);
				await this.updateDossierAccess();
				await this.updatePermissions();
				await this.updateProfilePicture();
				this.$wf.notify(this.$ct("common.saved"), "success", 3000, "check");
			}catch(err){
			}

			this.isSaving = false;
		},
		async saveUser(user:UserInfo){
			if (this.user.ID != 0){
				this.user = await CompanyService.updateUser(user, this.inputs.password);
			} else {
				this.user = await CompanyService.createUser(user, this.inputs.password);
				if (this.dossierAccess){
					await DossierService.addUserToDossier(this.user);
					this.hasDossierAccess = true;
				}
			}
			this.setInputsFromUser(this.user);
		},
		async updateDossierAccess(){
			if (this.hasDossierAccess != this.dossierAccess){
				if (this.dossierAccess){
					await DossierService.addUserToDossier(this.user);
				}else{
					await DossierService.removeFromDossier(this.user);
				}
			}
			this.hasDossierAccess = this.dossierAccess;
		},
		async updatePermissions(){
			if (this.user.ID == AuthService.wfUser.ID && !this.isOwner) {
				return;
			}
			if (this.hasDossierAccess){
				if (this.permissions && this.isOwner){
					await UserConfigService.setUsersHiddenButtons(this.user.ID, this.permissions);
				} else if (this.permissions){
					await UserConfigService.setUsersPermissions(this.user.ID, this.permissions);
				}
				this.permissions = await UserConfigService.getUsersPermissions(this.user.ID);
			}
		},
		async updateProfilePicture(){
			if (!this.newProfilePicture) return;
			let userInfo = await CompanyService.setUserProfileImg(this.user, this.newProfilePicture);
			this.user.ProfileImg = userInfo.ProfileImg;
			this.newProfilePicture = null;
			URL.revokeObjectURL(this.newProfilePictureUrl);
			this.newProfilePictureUrl = "";
		},
		setInputsFromUser(user:UserInfo){
			this.inputs.username = this.user.Username;
			this.inputs.email = this.user.Email;
			this.inputs.firstName = this.user.Firstname;
			this.inputs.lastName = this.user.Lastname;
			this.inputs.password = "";
			this.inputs.repeatPassword = "";
		},
		setUserFromInputs():UserInfo{
			let user = new UserInfo(this.user.getJSON());
			user.Username = this.inputs.username;
			user.Firstname = this.inputs.firstName;
			user.Lastname = this.inputs.lastName;
			user.Email = this.inputs.email;
			return user;
		},
		emailValidator():boolean{
			this.hasError.email = false;
			if (!this.emailRequired && this.inputs.email == ""){
				return true;
			}
			if (this.emailRequired && this.inputs.email == ""){
				if (this.usedInputs.email){
					this.hasError.email = true;
					this.formErrors.push("errors.email-required");
				}
				return false;
			}
			if (!isStringEmail(this.inputs.email)){
				if (this.usedInputs.email){
					this.hasError.email = true;
					this.formErrors.push("errors.email-is-not-valid");
				}
				return false;
			}
			return true;
		},
		passwordValidator():boolean{
			this.hasError.password = false;
			if (!this.passwordRequired && this.inputs.password == ""){
				this.hasError.password = false;
				return true;
			}
			if (!isPasswordStrongEnough(this.inputs.password)){
				if (this.usedInputs.password){
					this.hasError.password = true;
					if (this.inputs.password == ""){
						this.formErrors.push("errors.password-required");
					}else{
						this.formErrors.push("errors.password-not-strong-enough");
					}
				}
				return false;
			}
			return true;
		},
		samePasswordValidator():boolean{
			this.hasError.repeatPassword = false;
			if (this.inputs.password != this.inputs.repeatPassword){
				if (this.usedInputs.repeatPassword){
					this.hasError.repeatPassword = true;
					this.formErrors.push("errors.passwords-dont-match");
				}
				return false;
			}
			return true;
		},
		ipRestrictionsValidator():boolean{
			for (let ip of this.user.RestrictedToIPAddresses){
				if (!isIpAddressValid(ip)) return false;
			}
			return true;
		},
		onNewProfilePicture(value:File){
			this.newProfilePicture = value;
			this.newProfilePictureUrl = URL.createObjectURL(value);
		}
	},
	created(){
		this.validators = [
			this.emailValidator,
			this.passwordValidator,
			this.samePasswordValidator,
			this.ipRestrictionsValidator,
		];
		this.validate();
	},
	async mounted(){
		let id = this.$route.params.id;
		if (id){
			this.user = await CompanyService.getUser(parseInt(id));
			this.setInputsFromUser(this.user);
			this.hasDossierAccess = this.user.DossierIDs.find(d=>d == AuthService.wfDossier.id) != null;
			this.dossierAccess = this.hasDossierAccess;
			if (this.hasDossierAccess){
				this.permissions = await UserConfigService.getUsersPermissions(this.user.ID);
			}
		}
		this.loading = false;
	},
	destroyed(){
		URL.revokeObjectURL(this.newProfilePictureUrl);
	},
	components: {
		AppToolbar,
		AppMobileFullScreenContainer,
		AppPermissions,
		AppErrorMessage,
		AppChangeProfilePicture,
		AppUserEditIpRestriction
	},
	i18nEx: {
		componentPrefix: "components.settings-default"
	}
});
